import { useCallback } from "react";
import { useApiService } from "./apiService";

export type AuditLogSortField = "Timestamp" | "Action" | "EntityType" | "PerformedBy";

export interface AuditLogDto {
  id: string;
  action: string;
  entityType: string;
  entityId: string;
  performedBy: string;
  tenantId?: string | null;
  details: string;
  timestamp: string;
}

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface GetAuditLogsParams {
  page?: number;
  pageSize?: number;
  search?: string;
  action?: string;
  entityType?: string;
  /** ISO date (yyyy-MM-dd) — inclusive lower bound. */
  dateFrom?: string;
  /** ISO date (yyyy-MM-dd) — inclusive upper bound. */
  dateTo?: string;
  performedByUserId?: string;
  sortBy?: AuditLogSortField;
  sortDescending?: boolean;
  /** Tenant context override — required for Global Admins browsing a specific tenant. */
  tenantId?: string;
  isGlobalAdminContext?: boolean;
}

const emptyPagedResult = (page: number, pageSize: number): PagedResult<AuditLogDto> => ({
  items: [],
  totalCount: 0,
  page,
  pageSize,
});

export const useAuditLogService = () => {
  const apiService = useApiService();

  const getAuditLogs = useCallback(async (
    params: GetAuditLogsParams = {}
  ): Promise<PagedResult<AuditLogDto>> => {
    const page = params.page && params.page > 0 ? params.page : 1;
    const pageSize = params.pageSize && params.pageSize > 0 ? params.pageSize : 25;

    const query = new URLSearchParams();
    query.set("page", String(page));
    query.set("pageSize", String(pageSize));

    if (params.search?.trim()) {
      query.set("search", params.search.trim());
    }
    if (params.action) {
      query.set("action", params.action);
    }
    if (params.entityType) {
      query.set("entityType", params.entityType);
    }
    if (params.dateFrom) {
      query.set("dateFrom", params.dateFrom);
    }
    if (params.dateTo) {
      query.set("dateTo", params.dateTo);
    }
    if (params.performedByUserId) {
      query.set("performedByUserId", params.performedByUserId);
    }
    if (params.sortBy) {
      query.set("sortBy", params.sortBy);
    }
    if (params.sortDescending !== undefined) {
      query.set("sortDescending", String(params.sortDescending));
    }

    const requestHeaders: Record<string, string> = {};
    if (params.tenantId) {
      requestHeaders["X-Tenant-Id"] = params.tenantId;
    }
    if (params.isGlobalAdminContext !== undefined) {
      requestHeaders["X-Is-Global-Admin"] = String(params.isGlobalAdminContext);
    }

    const response = await apiService.apiCall<PagedResult<AuditLogDto>>({
      method: "GET",
      endpoint: `/api/audit-logs?${query.toString()}`,
      headers: Object.keys(requestHeaders).length ? requestHeaders : undefined,
      requireAuth: true,
    });

    return response.data ?? emptyPagedResult(page, pageSize);
  }, [apiService]);

  return { getAuditLogs };
};
